import { useEffect, useState } from 'react';
import { fromPairs, path, pipe, map, toPairs } from 'ramda';

import { getUserLimits } from '../client/dashboard/account';
import { isNilOrEmpty, removeUndefined } from '../utils';
import { getFirebase } from '../utils/signin';

const keysToUpperCase = pipe(
    toPairs,
    map(([k, v]) => [k.toUpperCase(), v]),
    fromPairs
);

const useUser = () => {
    const firebase = getFirebase();
    const [user, setUser] = useState(null);
    const [isSignedIn, setIsSignedIn] = useState(false);
    const [userHasPlan, setUserHasPlan] = useState(false);
    const [userPlan, setUserPlan] = useState(null);
    const [userTier, setUserTier] = useState(1);
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [userLimits, setUserLimits] = useState({});
    const [customer, setCustomer] = useState(null);
    const [customerIsLoading, setCustomerIsLoading] = useState(false);

    useEffect(() => {
        if (isNilOrEmpty(firebase)) return;

        // CONNECT TO FUNCTIONS LOCALLY, DO NOT USE FOR PRODUCTION
        if (process.env.GATSBY_FIREBASE_LOCALHOST == 1) {
            getFirebase().functions().useEmulator('127.0.0.1', 5001);
        }

        const unregisterAuthObserver = getFirebase()
            .auth()
            .onAuthStateChanged(async (newUser) => {
                setUser(newUser);
                const createdAt = path(['metadata', 'createdAt'], newUser);
                const limits = newUser && (await getUserLimits(newUser.uid, parseInt(createdAt)));
                setUserLimits(limits);
                setIsSignedIn(!!newUser);
                if (newUser) {
                    if (typeof window !== 'undefined') {
                        window.sendinblue?.identify(newUser.email, {
                            FIRSTNAME: newUser.displayName
                            // 'WEBSITE': 'www.foo.com'
                        });
                    }
                    await fetchCustomer(false, newUser?.uid, newUser?.email);
                    const db = firebase.firestore();
                    db.collection('customers')
                        .doc(newUser?.uid)
                        .collection('subscriptions')
                        .where('status', 'in', ['trialing', 'active'])
                        .onSnapshot(async (snapshot) => {
                            if (!snapshot.empty) {
                                setUserHasPlan(true);
                                const subscription = snapshot.docs[0].data();
                                const product = (await subscription.product.get()).data();
                                setUserPlan(product.metadata.plan);
                                setUserTier(product.metadata.tier);
                                return;
                            }
                        });
                }
                setIsLoadingUser(false);
            });
        return () => unregisterAuthObserver(); // Make sure we un-register Firebase observers when the component unmounts.
    }, []);

    const updateBrevoContact = (data) => {
        if (isNilOrEmpty(user) || isNilOrEmpty(data)) return;

        window.Brevo.push(function () {
            window.Brevo.identify({
                identifiers: {
                    email_id: user?.email
                },
                attributes: {
                    EXT_ID: user?.uid,
                    ...keysToUpperCase(data)
                }
            });
        });
    };

    const insertCustomerInitialData = () => {
        if (customer?.timezone && customer?.firstname && customer?.lastname) return {};

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const [firstname, lastname] = user.displayName.split(' ');
        return removeUndefined({ timezone, firstname, lastname });
    };

    const createCustomer = async (data = {}, isQuiet = false, userUid = user?.uid) => {
        setCustomerIsLoading(!isQuiet);
        const db = firebase.firestore();
        await db
            .collection('customers')
            .doc(userUid)
            .set(
                {
                    ...removeUndefined(data),
                    ...insertCustomerInitialData(),
                    createdAt: new Date().toISOString()
                },
                { merge: true }
            );
        await fetchCustomer(isQuiet);
        setCustomerIsLoading(false);
    };

    const fetchCustomer = async (isQuiet = false, userUid = user?.uid, email = null) => {
        setCustomerIsLoading(!isQuiet);
        const db = firebase.firestore();
        const doc = await db.collection('customers').doc(userUid).get();
        if (!doc.exists && email) {
            await db.collection('customers').doc(userUid).set({
                email
            });
        }
        setCustomer(doc.data());
        setCustomerIsLoading(false);
    };

    const updateCustomer = async (data, isQuiet = false, userUid = user?.uid) => {
        setCustomerIsLoading(!isQuiet);
        const db = firebase.firestore();
        const doc = await db.collection('customers').doc(userUid).get();
        await doc.ref.update({ ...removeUndefined(data), ...insertCustomerInitialData() });
        updateBrevoContact({ ...removeUndefined(data), ...insertCustomerInitialData() });
        await fetchCustomer(isQuiet);
        setCustomerIsLoading(false);
    };

    const trackEvent = async (step) => {
        try {
            const track = firebase.functions().httpsCallable('brev');
            await track({ nm: step });
        } catch (e) {}
    };

    return {
        user,
        isSignedIn,
        userHasPlan,
        isLoadingUser,
        userLimits,
        userPlan,
        userTier,
        customer,
        customerIsLoading,
        createCustomer,
        fetchCustomer,
        updateCustomer,
        trackEvent
    };
};

export { useUser };
